import React, { useRef, useState, useEffect } from 'react';
import { SwatchesPicker } from 'react-color';
import { toast } from 'react-toastify';
import { Popover, PopoverBody } from 'reactstrap';
import { Check, Close, Create, CropSquare, RadioButtonUnchecked, TextFields, RemoveSharp, ColorLens } from '@material-ui/icons';
import styles from '../../modules/forms/forms.module.scss';
import Button from '../button/button';
import dragImage from '../../icons/dragable.svg';
import { GetFileName, S3Uploads } from '../../modules/services';
import Loading from '../loading';

const MIN_SHAPE_SIZE = 20;

const ensureMinShapeDelta = (delta) => {
    if (Math.abs(delta) < MIN_SHAPE_SIZE) {
        return delta >= 0 ? MIN_SHAPE_SIZE : -MIN_SHAPE_SIZE;
    }
    return delta;
};

const getAdjustedShapeEnd = (start, pointer) => ({
    x: start.x + ensureMinShapeDelta(pointer.x - start.x),
    y: start.y + ensureMinShapeDelta(pointer.y - start.y)
});

const getBoundingBox = (start, end) => {
    if (!start || !end) return null;
    return {
        minX: Math.min(start.x, end.x),
        minY: Math.min(start.y, end.y),
        maxX: Math.max(start.x, end.x),
        maxY: Math.max(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y)
    };
};

const drawTickPath = (ctx, start, end) => {
    const box = getBoundingBox(start, end);
    if (!box) return;
    const width = Math.max(box.width, MIN_SHAPE_SIZE);
    const height = Math.max(box.height, MIN_SHAPE_SIZE);
    const startX = box.minX + width * 0.1;
    const startY = box.minY + height * 0.55;
    const midX = box.minX + width * 0.45;
    const midY = box.minY + height * 0.9;
    const endX = box.minX + width * 0.95;
    const endY = box.minY + height * 0.25;

    ctx.moveTo(startX, startY);
    ctx.lineTo(midX, midY);
    ctx.lineTo(endX, endY);
};

const drawCrossPath = (ctx, start, end) => {
    const box = getBoundingBox(start, end);
    if (!box) return;
    ctx.moveTo(box.minX, box.minY);
    ctx.lineTo(box.maxX, box.maxY);
    ctx.moveTo(box.minX, box.maxY);
    ctx.lineTo(box.maxX, box.minY);
};

const isInsideShapeBounds = (x, y, path) => {
    if (!path?.startPos || !path?.endPos) return false;
    const minX = Math.min(path.startPos.x, path.endPos.x);
    const maxX = Math.max(path.startPos.x, path.endPos.x);
    const minY = Math.min(path.startPos.y, path.endPos.y);
    const maxY = Math.max(path.startPos.y, path.endPos.y);
    return x >= minX && x <= maxX && y >= minY && y <= maxY;
};

const Annotations = ({ imgSrc, handleSavePath, handleFieldErrorChanged, existingPaths, drawMode, annotateId, existingImgSrc }) => {
    const canvasRef = useRef(null);
    const [ctx, setCtx] = useState(null);
    const [drawingTool, setDrawingTool] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [paths, setPaths] = useState(existingPaths?.length > 0 ? existingPaths: []);
    const [redoPaths, setRedoPaths] = useState([]);
    const [textInput, setTextInput] = useState('');
    const [textPosition, setTextPosition] = useState(null);
    const [drawColor, setDrawColor] = useState('#000');
    const [isOpenPopover, setIsOpenPopover] = useState(false);
    const [lineWidth] = useState(2);
    const [default_fontSize, setFontSize] = useState(20);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [draggedElementIndex, setDraggedElementIndex] = useState(null);
    const [dragMode, setDragMode] = useState(false);
    const [imgUrl, setImgUrl] = useState('');
    const [imgLoading, setImgLoading] = useState(false);
    const [imgCanvas, setImgCanvas] = useState(null);
    const [orginalImgCanvas, setOrginalImgCanvas] = useState(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && imgUrl === '') {
            setImgLoading(true);
            canvas.width = canvas.offsetWidth;
            const image = new Image();
            image.crossOrigin = 'anonymous'; // Enable CORS
            image.src = imgSrc || imgUrl;
            image.onload = () => {
                const imgWidth = image.width;
                const imgHeight = image.height;
                const aspectRatio = imgWidth / imgHeight;
                canvas.height = canvas.width / aspectRatio;
                setCtx(canvas.getContext('2d'));
                setImgUrl(imgSrc);
                setImgLoading(false);
            };
            setImgCanvas(image);
        }
        if (canvas && existingImgSrc && orginalImgCanvas === null) {
            const orginalImage = new Image();
            orginalImage.crossOrigin = 'anonymous';
            orginalImage.src = existingImgSrc;
            orginalImage.onload = () => {
                setImgLoading(false);
            };
            setOrginalImgCanvas(orginalImage);
        }
    }, [imgSrc, imgUrl, existingImgSrc, orginalImgCanvas]);

    useEffect(() => {
        if (ctx) {
            redrawAll();
            setImgLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paths, ctx]);

    const findClickedElementIndex = (x, y) => paths?.findIndex((path) => {
            switch (path.type) {
                case 'text':
                    return isInsideTextBox(x, y, path);
                case 'line':
                    return isNearLine(x, y, path);
                case 'rectangle':
                    return isInsideRectangle(x, y, path);
                case 'circle':
                    return isInsideCircle(x, y, path);
                case 'tick':
                case 'cross':
                    return isInsideShapeBounds(x, y, path);
                default:
                    return false;
            }
    });

    const isInsideTextBox = (x, y, path) => x >= path?.x && x <= path?.x + 100 && y >= path?.y - 20 && y <= path?.y;

    const isNearLine = (x, y, path) => pointToLineDistance({ x, y }, path?.startPos, path?.endPos) <= 5;

    const isInsideRectangle = (x, y, path) => {
        // Get the correct bounding box for the rectangle
        const minX = Math.min(path.startPos.x, path.endPos.x);
        const maxX = Math.max(path.startPos.x, path.endPos.x);
        const minY = Math.min(path.startPos.y, path.endPos.y);
        const maxY = Math.max(path.startPos.y, path.endPos.y);
        // Check if the point is inside the bounds of the rectangle
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
    };

    const isInsideCircle = (x, y, path) => {
        const distance = Math.sqrt((x - path?.center?.x) ** 2 + (y - path?.center?.y) ** 2);
        return distance <= path?.radius;
    };

    // Function to calculate the distance from a point to a line segment
    const pointToLineDistance = (point, lineStart, lineEnd) => {
        const { x: x0, y: y0 } = point;
        const { x: x1, y: y1 } = lineStart;
        const { x: x2, y: y2 } = lineEnd;

        const A = x0 - x1;
        const B = y0 - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const len_sq = C * C + D * D;
        const param = len_sq !== 0 ? dot / len_sq : -1;

        let xx; let yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = x0 - xx;
        const dy = y0 - yy;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const handleMouseDown = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        if (dragMode) {
            // Check if clicking on any existing path to drag
            const clickedElementIndex = findClickedElementIndex(x, y);
            if (clickedElementIndex !== -1) {
                setDraggedElementIndex(clickedElementIndex);
                setIsDraggingText(true);
                setStartPos({ x, y });
                return;
            }
        }
        if (!drawingTool) return;
        setStartPos({ x, y });
        setCurrentPos({ x, y });
        setIsDrawing(true);

        if (drawingTool === 'text') {
            setTextPosition({ x, y });
            setTextInput('');
        } else if (drawingTool === 'sign') {
            ctx.beginPath();
            ctx.moveTo(x, y);
            setPaths([...paths, { type: 'sign', points: [[x, y]], color: drawColor }]);
        }
    };

    const handleMouseMove = (event) => {
        if (!isDrawing && !isDraggingText) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        if (isDraggingText && draggedElementIndex !== null) {
            const dx = x - startPos.x;
            const dy = y - startPos.y;

            const updatedPaths = [...paths];
            const draggedElement = updatedPaths[draggedElementIndex];

            if (draggedElement.type === 'text') {
                draggedElement.x += dx;
                draggedElement.y += dy;
            } else if (draggedElement.type === 'line') {
                draggedElement.startPos.x += dx;
                draggedElement.startPos.y += dy;
                draggedElement.endPos.x += dx;
                draggedElement.endPos.y += dy;
            } else if (draggedElement.type === 'rectangle') {
                draggedElement.startPos.x += dx;
                draggedElement.startPos.y += dy;
                draggedElement.endPos.x += dx;
                draggedElement.endPos.y += dy;
            } else if (draggedElement.type === 'circle') {
                draggedElement.center.x += dx;
                draggedElement.center.y += dy;
            } else if (draggedElement.type === 'tick' || draggedElement.type === 'cross') {
                draggedElement.startPos.x += dx;
                draggedElement.startPos.y += dy;
                draggedElement.endPos.x += dx;
                draggedElement.endPos.y += dy;
            }

            setPaths(updatedPaths);
            setStartPos({ x, y });
            redrawAll();
        }

        if (!isDrawing || !drawingTool) return;
        setCurrentPos({ x, y });
        ctx.lineWidth = lineWidth;

        const shouldClearPreview = ['line', 'rectangle', 'circle', 'tick', 'cross'].includes(drawingTool);
        if (shouldClearPreview) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            redrawAll();
        }
        ctx.strokeStyle = drawColor;

        if (drawingTool === 'line') {
            ctx.beginPath();
            ctx.moveTo(startPos.x, startPos.y);
            ctx.lineTo(x, y);
            ctx.stroke();
        } else if (drawingTool === 'rectangle') {
            ctx.beginPath();
            ctx.rect(startPos.x, startPos.y, x - startPos.x, y - startPos.y);
            ctx.stroke();
        } else if (drawingTool === 'circle') {
            const radius = Math.sqrt((x - startPos.x) ** 2 + (y - startPos.y) ** 2);
            ctx.beginPath();
            ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
            ctx.stroke();
        } else if (drawingTool === 'tick') {
            ctx.beginPath();
            const previewEnd = getAdjustedShapeEnd(startPos, { x, y });
            drawTickPath(ctx, startPos, previewEnd);
            ctx.stroke();
        } else if (drawingTool === 'cross') {
            ctx.beginPath();
            const previewEnd = getAdjustedShapeEnd(startPos, { x, y });
            drawCrossPath(ctx, startPos, previewEnd);
            ctx.stroke();
        } else if (drawingTool === 'sign') {
            const newPaths = paths.slice();
            const currentPath = newPaths[newPaths.length - 1];
            if (currentPath && currentPath.type === 'sign') {
                currentPath.points.push([x, y]);
                setPaths(newPaths);
                ctx.lineTo(x, y);
                ctx.stroke();
            }
        }
    };

    const handleMouseUp = () => {
        if (isDraggingText) {
            setIsDraggingText(false);
            setDraggedElementIndex(null);
            return;
        }
        if (!isDrawing || !drawingTool) return;

        setIsDrawing(false);
        ctx.closePath();
        const pathColor = drawColor;

        if (drawingTool === 'line') {
            setPaths([...paths, { type: 'line', startPos, endPos: currentPos, color: pathColor }]);
        } else if (drawingTool === 'rectangle') {
            setPaths([...paths, { type: 'rectangle', startPos, endPos: currentPos, color: pathColor }]);
        } else if (drawingTool === 'circle') {
            const radius = Math.sqrt((currentPos.x - startPos.x) ** 2 + (currentPos.y - startPos.y) ** 2);
            setPaths([...paths, { type: 'circle', center: startPos, radius, color: pathColor }]);
        } else if (drawingTool === 'tick') {
            const endPos = getAdjustedShapeEnd(startPos, currentPos);
            setPaths([...paths, {
                type: 'tick',
                startPos: { ...startPos },
                endPos,
                color: pathColor
            }]);
        } else if (drawingTool === 'cross') {
            const endPos = getAdjustedShapeEnd(startPos, currentPos);
            setPaths([...paths, {
                type: 'cross',
                startPos: { ...startPos },
                endPos,
                color: pathColor
            }]);
        } else if (drawingTool === 'sign') {
            const newPaths = paths.slice();
            setPaths(newPaths);
        }

        setRedoPaths([]);
    };

    const redrawAll = () => {
        if (!ctx) return;
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        if (paths?.length > 0) {
            paths.forEach((path) => {
                ctx.beginPath();
                ctx.strokeStyle = path.color || drawColor;
                ctx.lineWidth = lineWidth; // Set line width
                if (path.type === 'line') {
                    ctx.moveTo(path.startPos.x, path.startPos.y);
                    ctx.lineTo(path.endPos.x, path.endPos.y);
                    ctx.stroke();
                } else if (path.type === 'rectangle') {
                    ctx.rect(
                        path.startPos.x,
                        path.startPos.y,
                        path.endPos.x - path.startPos.x,
                        path.endPos.y - path.startPos.y
                    );
                    ctx.stroke();
                } else if (path.type === 'circle') {
                    ctx.arc(path.center.x, path.center.y, path.radius, 0, 2 * Math.PI);
                    ctx.stroke();
                } else if (path.type === 'tick') {
                    drawTickPath(ctx, path.startPos, path.endPos);
                    ctx.stroke();
                } else if (path.type === 'cross') {
                    drawCrossPath(ctx, path.startPos, path.endPos);
                    ctx.stroke();
                } else if (path.type === 'sign') {
                    ctx.moveTo(path.points[0][0], path.points[0][1]);
                    path.points.forEach(([x, y]) => ctx.lineTo(x, y));
                    ctx.stroke();
                } else if (path.type === 'text') {
                    ctx.font = `${path.fontSize || default_fontSize}px Arial`;
                    ctx.fillStyle = path.color || drawColor;
                    ctx.fillText(path.text, path.x, path.y);
                }
            });
        }
    };

    const handleUploadAttachments = async (newAttachments, entity) => {
        setImgLoading(true);
        const attachmentIds = [];
        const oldAttachments = [];
        let fileIds = '';
        if (newAttachments?.length > 0) {
          fileIds = await GetFileName.getUploadFileName(newAttachments?.length);
        }
        if (fileIds) {
          await S3Uploads.uploadFiles(
            newAttachments,
            fileIds.filename,
            entity || 'forms',
            false
          ).then(() => {
            fileIds.filename.forEach((id) => {
              attachmentIds.push(id.toString());
            });
          });
        }
        if (oldAttachments.length > 0) {
          attachmentIds.push(...oldAttachments);
        }
        setImgLoading(false);
        return attachmentIds;
    };

    const handleSave = async () => {
        setImgLoading(true);
        const canvas = canvasRef.current;
        const tempCtx = canvas.getContext('2d');
        tempCtx.clearRect(0, 0, canvas.width, canvas.height);
        if (canvas && imgCanvas && tempCtx) {
            tempCtx.drawImage(imgCanvas, 0, 0, canvas.width, canvas.height);
            // Redraw all paths (annotations) onto the canvas
            if (paths?.length > 0) {
                paths.forEach((path) => {
                    tempCtx.beginPath();
                    tempCtx.strokeStyle = path.color || drawColor;
                    tempCtx.lineWidth = lineWidth;
                    if (path.type === 'line') {
                        tempCtx.moveTo(path.startPos.x, path.startPos.y);
                        tempCtx.lineTo(path.endPos.x, path.endPos.y);
                        tempCtx.stroke();
                    } else if (path.type === 'rectangle') {
                        tempCtx.rect(
                            path.startPos.x,
                            path.startPos.y,
                            path.endPos.x - path.startPos.x,
                            path.endPos.y - path.startPos.y
                        );
                        tempCtx.stroke();
                    } else if (path.type === 'circle') {
                        tempCtx.arc(path.center.x, path.center.y, path.radius, 0, 2 * Math.PI);
                        tempCtx.stroke();
                    } else if (path.type === 'tick') {
                        drawTickPath(tempCtx, path.startPos, path.endPos);
                        tempCtx.stroke();
                    } else if (path.type === 'cross') {
                        drawCrossPath(tempCtx, path.startPos, path.endPos);
                        tempCtx.stroke();
                    } else if (path.type === 'sign') {
                        tempCtx.moveTo(path.points[0][0], path.points[0][1]);
                        path.points.forEach(([x, y]) => tempCtx.lineTo(x, y));
                        tempCtx.stroke();
                    } else if (path.type === 'text') {
                        tempCtx.font = `${path.fontSize || default_fontSize}px Arial`;
                        tempCtx.fillStyle = path.color || drawColor;
                        tempCtx.fillText(path.text, path.x, path.y);
                    }
                });
            }

            // Now generate the image URL from the canvas in PNG format
            const imageURL = canvas.toDataURL('image/png');
            let imageData = await fetch(imageURL);
            let blobData = await imageData.blob();
            const url = URL.createObjectURL(blobData);
            const file = new File([blobData], 'unnamed.png', {
                preview: url,
                type: blobData.type,
                lastModified: Date.now(),
                lastModifiedDate: new Date()
            });
            let annotate_attachment = [];
            if (file && url) {
                annotate_attachment = await handleUploadAttachments([file], 'form_annotation');
            }
            toast.success('Saved successfully.', { position: 'top-right' });
            setImgLoading(false);
            // Continue saving paths if necessary
            handleSavePath(annotate_attachment?.length > 0 ? annotate_attachment : paths || []);
            handleFieldErrorChanged(!paths?.length > 0);
        }
    };

    const handleTextSubmit = () => {
        if (textInput && textPosition) {
            const { x, y } = textPosition;
            ctx.font = `${default_fontSize}px Arial`;
            ctx.fillStyle = 'black';
            ctx.fillText(textInput, x, y);
            setPaths([...paths, { type: 'text', x, y, text: textInput, color: drawColor, fontSize: default_fontSize }]);
            setTextPosition(null);
            setTextInput('');
            setIsDrawing(false);
        }
    };

    const handleClear = () => {
        if (existingImgSrc && orginalImgCanvas) {
            setImgUrl(existingImgSrc);
            setImgCanvas(orginalImgCanvas);
        }
        handleSavePath([]);
        handleFieldErrorChanged(true);
        setPaths([]);
        setRedoPaths([]);
        const canvas = canvasRef.current;
        const tempCtx = canvas.getContext('2d');
        tempCtx.clearRect(0, 0, canvas.width, canvas.height);
        setDrawingTool(null);
        setIsDrawing(false);
        setIsOpenPopover(false);
        setIsDraggingText(false);
        setDragMode(false);
        setDraggedElementIndex(null);
    };

    const handleUndo = () => {
        if (paths?.length === 0) return;
        const lastPath = paths.pop();
        setRedoPaths([lastPath, ...redoPaths]);
        setPaths(paths.slice());
        redrawAll();
    };

    const handleRedo = () => {
        if (redoPaths?.length === 0) return;
        const lastUndonePath = redoPaths.shift();
        setPaths([...paths, lastUndonePath]);
        setRedoPaths(redoPaths.slice());
        redrawAll();
    };

    const clickDrawingTool = (val) => {
        setIsDrawing(false);
        setDrawingTool(val);
        setIsOpenPopover(false);
        setIsDraggingText(false);
        setDragMode(false);
        setDraggedElementIndex(null);
    };

    const toggleDragMode = () => {
        setDragMode(!dragMode);
        setIsDrawing(false);
        setDrawingTool(null);
    };

    return (
        <div className={styles.imagePosition}>
            <Loading loadingStatus={imgLoading} />
            {drawMode && (
                <div className={styles.annotateIcons}>
                    <button tabIndex={0} onClick={() => clickDrawingTool('line')} title="Line">
                        <RemoveSharp style={{ transform: 'rotate(60deg)' }} />
                    </button>
                    <button tabIndex={0} onClick={() => clickDrawingTool('rectangle')} title="Rectangle">
                        <CropSquare />
                    </button>
                    <button tabIndex={0} onClick={() => clickDrawingTool('circle')} title="Circle">
                        <RadioButtonUnchecked />
                    </button>
                    <button onClick={() => clickDrawingTool('tick')} title="Tick">
                        <Check />
                    </button>
                    <button onClick={() => clickDrawingTool('cross')} title="Cross">
                        <Close />
                    </button>
                    <button tabIndex={0} onClick={() => clickDrawingTool('text')} title="Text">
                        <TextFields />
                    </button>
                    <button tabIndex={0} onClick={() => clickDrawingTool('sign')} title="Freeform">
                        <Create />
                    </button>
                    <button>
                        <div id={`popover${annotateId}`} style={{ cursor: 'pointer' }}>
                            <ColorLens />
                        </div>
                        <Popover
                            placement="right"
                            isOpen={isOpenPopover}
                            target={`popover${annotateId}`}
                            toggle={() => setIsOpenPopover(!isOpenPopover)}
                            className={styles.desPopover}
                        >
                            <PopoverBody style={{ padding: '10px 10px 10px 15px' }}>
                                <SwatchesPicker
                                    onChange={(color) => {
                                        setDrawColor(color.hex);
                                        setIsOpenPopover(false);
                                    }}
                                />
                            </PopoverBody>
                        </Popover>
                    </button>
                    <button onClick={toggleDragMode} title="Drag & Drop" style={{ cursor: 'pointer' }}>
                        <img src={dragImage} alt="dragImage" />
                    </button>
                    <input
                        title="Font Size"
                        type="number"
                        value={default_fontSize}
                        onChange={e => setFontSize(e.target.value)}
                        min={0}
                        className={styles.fontSyle}
                    />
                </div>)}
            <div className={styles.imagePosition} style={{ width: '100%', height: '100%' }}>
                <canvas
                    className={styles.imageCanvas}
                    ref={canvasRef}
                    style={{
                        backgroundImage: `url(${imgUrl})`,
                        backgroundSize: 'cover',
                        top: 0,
                        left: 0,
                        pointerEvents: 'auto',
                        width: '100%',
                        height: '100%'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                />
                {drawingTool === 'text' && textPosition && (
                    <input
                        type="text"
                        value={textInput}
                        onChange={e => setTextInput(e.target.value)}
                        onBlur={handleTextSubmit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleTextSubmit();
                        }}
                        style={{
                            position: 'absolute',
                            top: textPosition.y,
                            left: textPosition.x,
                            fontSize: `${default_fontSize}px`,
                            border: `1px solid ${drawColor}`,
                            color: drawColor,
                            padding: '2px',
                            background: 'rgba(255, 255, 255, 0.7)',
                            height: '40px',
                            width: '250px'
                        }}
                    />
                )}
            </div>
            {drawMode && (
                <div className={styles.canvasButton}>
                    <Button action={'secondary'} onClick={handleSave}
                        translationID={'shifts.timepicker.save'}
                        defaultText={'Save'} className={styles.canvasMargin}
                    />
                    <Button action={'tertiary'} onClick={handleClear}
                        translationID={'filters.form.clear'}
                        defaultText={'Clear'} className={styles.canvasMargin}
                    />
                    <Button action={'tertiary'} onClick={handleUndo}
                        translationID={'annotation.form.undo'}
                        defaultText={'Undo'} className={styles.canvasMargin}
                    />
                    <Button action={'tertiary'} onClick={handleRedo}
                        translationID={'annotation.form.redo'}
                        defaultText={'Redo'} className={styles.canvasMargin}
                    />
                </div>)}
        </div>
    );
};

export default Annotations;
